import { validate } from 'rut.js';
import { validateEmail } from '../../utils/validator';
import { validateEmailExists } from '../../requests/api/students';
import { EnrollmentFormData } from './types';
import { StepConfig } from './formConfig';

export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

// Función para calcular la edad basada en la fecha de nacimiento
export const calculateAge = (birthDate: string | Date): number => {
  if (!birthDate) return 0;

  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }

  return age;
};

// Validaciones específicas por campo
const validateField = (
  fieldName: string,
  value: unknown,
  enrollmentSteps: StepConfig[],
  formData: EnrollmentFormData,
): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Validaciones comunes para campos requeridos
  if (!value || (typeof value === 'string' && value.trim() === '')) {
    const currentStep = enrollmentSteps.find(
      (step) => step.fields.some((field) => field.name === fieldName),
    );
    const field = currentStep?.fields.find((f) => f.name === fieldName);

    if (field?.required) {
      let message = `${field.label} es requerido`;

      if (fieldName === 'leadName') {
        if (formData.leadSource === 'hermano') {
          message = 'Nombre del hermano/a es requerido';
        } else if (formData.leadSource === 'profesor') {
          message = 'Nombre del profesor/a es requerido';
        }
      }

      if (fieldName === 'ambassadorCode') {
        if (formData.leadSource === 'embajador') {
          message = 'Código del embajador/a es requerido';
        } else if (formData.leadSource === 'centro_alumnos') {
          message = 'Código del centro de alumnos es requerido';
        }
      }

      errors.push({
        field: fieldName,
        message,
      });
      return errors; // Si está vacío y es requerido, no validar más
    }
  }

  // Validaciones específicas por tipo de campo
  switch (fieldName) {
    case 'rut':
    case 'representativeRut':
    case 'supporterRut':
      if (value && typeof value === 'string' && !validate(value)) {
        errors.push({
          field: fieldName,
          message: 'El RUT ingresado no es válido',
        });
      }
      break;

    case 'email':
    case 'representativeEmail':
    case 'supporterEmail':
      if (value && typeof value === 'string' && !validateEmail(value)) {
        errors.push({
          field: fieldName,
          message: 'El correo electrónico no es válido',
        });
      }
      break;

    case 'birthDate':
      if (value && (typeof value === 'string' || value instanceof Date)) {
        const age = calculateAge(value);
        if (age < 15) {
          errors.push({
            field: fieldName,
            message: 'El estudiante debe tener al menos 15 años',
          });
        }
      }
      break;

    case 'representativeBirthDate':
    case 'supporterBirthDate':
      if (value && (typeof value === 'string' || value instanceof Date)) {
        const age = calculateAge(value);
        if (age < 18) {
          const label = fieldName.includes('representative') ? 'apoderado' : 'sostenedor';
          errors.push({
            field: fieldName,
            message: `El ${label} debe ser mayor de edad (18 años)`,
          });
        }
      }
      break;

    case 'phoneNumber':
    case 'representativePhoneNumber':
    case 'supporterPhoneNumber':
      if (value && typeof value === 'string') {
        // Validar formato de teléfono chileno (puede empezar con +56 o 56 o 9)
        const phoneRegex = /^(\+?56|56)?[9][0-9]{8}$/;
        if (!phoneRegex.test(value.replace(/\s/g, ''))) {
          errors.push({
            field: fieldName,
            message: 'El teléfono debe tener un formato válido (ej: +56912345678 o 912345678)',
          });
        }
      }
      break;

    case 'name':
    case 'lastname':
    case 'motherLastname':
    case 'representativeName':
    case 'representativeLastname':
    case 'representativeMotherLastname':
    case 'supporterName':
    case 'supporterLastname':
    case 'supporterMotherLastname':
      if (value && typeof value === 'string') {
        // Validar que solo contenga letras, espacios y acentos
        const nameRegex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/;
        if (!nameRegex.test(value)) {
          errors.push({
            field: fieldName,
            message: 'Solo se permiten letras y espacios',
          });
        }
        if (value.length < 2) {
          errors.push({
            field: fieldName,
            message: 'Debe tener al menos 2 caracteres',
          });
        }
      }
      break;
    case 'inscriptionYear':
      if (value !== null && value !== undefined && value !== '') {
        if (typeof value !== 'number' || Number.isNaN(value)) {
          errors.push({
            field: fieldName,
            message: 'El año debe ser un número válido',
          });
        }
      }
      break;

    default:
      break;
  }

  return errors;
};

const validateCrossEntityDuplicates = (formData: EnrollmentFormData): ValidationError[] => {
  const errors: ValidationError[] = [];
  const isSameAsRep = formData.isSameAsRepresentative;

  const ruts = [
    { label: 'alumno', field: 'rut', value: formData.rut?.trim() || '' },
    { label: 'apoderado', field: 'representativeRut', value: formData.representativeRut?.trim() || '' },
    ...(!isSameAsRep ? [{ label: 'sostenedor', field: 'supporterRut', value: formData.supporterRut?.trim() || '' }] : []),
  ];

  const emails = [
    { label: 'alumno', field: 'email', value: formData.email?.trim().toLowerCase() || '' },
    { label: 'apoderado', field: 'representativeEmail', value: formData.representativeEmail?.trim().toLowerCase() || '' },
    ...(!isSameAsRep ? [{ label: 'sostenedor', field: 'supporterEmail', value: formData.supporterEmail?.trim().toLowerCase() || '' }] : []),
  ];

  const phones = [
    { label: 'alumno', field: 'phoneNumber', value: formData.phoneNumber?.replace(/\s/g, '') || '' },
    { label: 'apoderado', field: 'representativePhoneNumber', value: formData.representativePhoneNumber?.replace(/\s/g, '') || '' },
    ...(!isSameAsRep ? [{ label: 'sostenedor', field: 'supporterPhoneNumber', value: formData.supporterPhoneNumber?.replace(/\s/g, '') || '' }] : []),
  ];

  const checkDuplicates = (
    entries: { label: string; field: string; value: string }[],
    fieldLabel: string,
  ) => {
    for (let i = 0; i < entries.length; i += 1) {
      for (let j = i + 1; j < entries.length; j += 1) {
        if (entries[i].value && entries[j].value && entries[i].value === entries[j].value) {
          errors.push({
            field: entries[i].field,
            message: `El ${fieldLabel} del ${entries[i].label} no puede ser igual al del ${entries[j].label}`,
          });
          errors.push({
            field: entries[j].field,
            message: `El ${fieldLabel} del ${entries[j].label} no puede ser igual al del ${entries[i].label}`,
          });
        }
      }
    }
  };

  checkDuplicates(ruts, 'RUT');
  checkDuplicates(emails, 'correo');
  checkDuplicates(phones, 'teléfono');

  return errors;
};

// Validaciones específicas por paso
const validateStep2 = (formData: EnrollmentFormData): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Validar que al menos una materia esté seleccionada
  const hasSelectedSubject = formData.selectedSubjects.length > 0;
  if (!hasSelectedSubject) {
    errors.push({
      field: 'selectedSubjects',
      message: 'Debes seleccionar al menos una materia a rendir',
    });
  }

  return errors;
};

// Función principal de validación por paso
export const validateStep = (
  stepNumber: number,
  formData: EnrollmentFormData,
  enrollmentSteps: StepConfig[],
): ValidationResult => {
  const errors: ValidationError[] = [];

  // Obtener la configuración del paso actual
  const currentStepConfig = enrollmentSteps.find((step) => step.stepNumber === stepNumber);

  if (!currentStepConfig) {
    return { isValid: false, errors: [{ field: 'step', message: 'Paso no válido' }] };
  }

  // Validar cada campo del paso actual
  currentStepConfig.fields.forEach((field) => {
    // Si es el paso 5 (sostenedor) y está marcado "Es el mismo que el apoderado",
    // no validar los campos del sostenedor excepto el checkbox
    if (stepNumber === 5 && formData.isSameAsRepresentative
      && field.name.includes('supporter') && field.name !== 'isSameAsRepresentative') {
      return; // Saltar validación para este campo
    }

    // Si es el paso 6, validar campos condicionales según leadSource
    if (stepNumber === 6) {
      // No validar ambassadorCode si no se seleccionó Embajador(a) o Centro de alumnos
      if (field.name === 'ambassadorCode'
          && formData.leadSource !== 'embajador'
          && formData.leadSource !== 'centro_alumnos') {
        return; // Saltar validación para este campo
      }

      // No validar leadName si no se seleccionó Hermano(a) o Profesor(a)
      if (field.name === 'leadName'
          && formData.leadSource !== 'hermano'
          && formData.leadSource !== 'profesor') {
        return; // Saltar validación para este campo
      }
    }

    const fieldValue = formData[field.name as keyof EnrollmentFormData];
    const fieldErrors = validateField(
      field.name,
      fieldValue,
      enrollmentSteps,
      formData,
    );
    errors.push(...fieldErrors);
  });

  // Validaciones específicas por paso
  if (stepNumber === 2) {
    const step2Errors = validateStep2(formData);
    errors.push(...step2Errors);
  }

  // Validar duplicados cruzados al avanzar en el paso del apoderado o sostenedor
  if (stepNumber === 4 || stepNumber === 5) {
    const stepFields = stepNumber === 4
      ? ['representativeRut', 'representativeEmail', 'representativePhoneNumber']
      : ['supporterRut', 'supporterEmail', 'supporterPhoneNumber'];

    const crossErrors = validateCrossEntityDuplicates(formData)
      .filter((e) => stepFields.includes(e.field));
    errors.push(...crossErrors);
  }

  // Validación genérica para campos con showWhen o showWhenSchoolIsOtro
  currentStepConfig.fields.forEach((field) => {
    // Validar otherSchoolName si eligió "Otro" en schoolId (solo si es requerido)
    if (field.showWhenSchoolIsOtro && stepNumber === 1 && field.required) {
      const schoolField = currentStepConfig.fields.find((f) => f.name === 'schoolId');
      const selectedOption = schoolField?.options?.find(
        (o) => String(o.value) === String(formData.schoolId),
      );
      if (selectedOption?.label === 'Otro') {
        const fieldValue = formData[field.name as keyof EnrollmentFormData];
        if (!fieldValue || (typeof fieldValue === 'string' && fieldValue.trim() === '')) {
          errors.push({
            field: field.name,
            message: 'Debes indicar el nombre de tu colegio',
          });
        }
      }
    }

    // Validar campos con showWhen (solo si son requeridos)
    if (field.showWhen && field.required) {
      const { field: conditionField, hasLabel } = field.showWhen;
      const targetField = currentStepConfig.fields.find((f) => f.name === conditionField);
      const targetFieldValue = formData[conditionField as keyof EnrollmentFormData];
      const selectedOption = targetField?.options?.find(
        (o) => String(o.value) === String(targetFieldValue),
      );
      if (selectedOption?.label === hasLabel) {
        const fieldValue = formData[field.name as keyof EnrollmentFormData];
        if (!fieldValue || (typeof fieldValue === 'string' && fieldValue.trim() === '')) {
          errors.push({
            field: field.name,
            message: `Debes indicar ${field.label?.toLowerCase()}`,
          });
        }
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Función para validar todo el formulario
export const validateAllSteps = (
  formData: EnrollmentFormData,
  enrollmentSteps: StepConfig[],
): ValidationResult => {
  const allErrors: ValidationError[] = [];

  for (let stepNumber = 1; stepNumber <= 6; stepNumber += 1) {
    const stepValidation = validateStep(stepNumber, formData, enrollmentSteps);
    allErrors.push(...stepValidation.errors);
  }

  const crossErrors = validateCrossEntityDuplicates(formData);
  allErrors.push(...crossErrors);

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
  };
};

// Función para validar emails con el backend
export const validateEmailWithBackend = async (
  fieldName: string,
  email: string,
  formData: EnrollmentFormData,
  setEmailValidations: React.Dispatch<React.SetStateAction<Record<string, {
    isValidating: boolean;
    message: string;
    isValid: boolean;
    canContinue: boolean;
  }>>>,
) => {
  if (!email || !email.includes('@')) {
    setEmailValidations((prev) => ({
      ...prev,
      [fieldName]: {
        isValidating: false, message: '', isValid: true, canContinue: true,
      },
    }));
    return;
  }

  // Marcar como validando
  setEmailValidations((prev) => ({
    ...prev,
    [fieldName]: {
      isValidating: true, message: '', isValid: true, canContinue: true,
    },
  }));

  try {
    let context: 'student' | 'representative' | undefined;
    let identityData: { rut?: string } | undefined;

    // Determinar contexto según el campo
    if (fieldName === 'email') {
      context = 'student';
      identityData = {
        rut: formData.rut,
      };
    } else if (fieldName.includes('representative') || fieldName.includes('supporter')) {
      context = 'representative';

      // Obtener datos de identidad para verificación
      if (fieldName.includes('representative')) {
        identityData = {
          rut: formData.representativeRut,
        };
      } else if (fieldName.includes('supporter')) {
        identityData = {
          rut: formData.supporterRut,
        };
      }
    }

    const result = await validateEmailExists(email, context, identityData);

    setEmailValidations((prev) => ({
      ...prev,
      [fieldName]: {
        isValidating: false,
        message: result.canContinue === false ? (result.message || '') : '',
        isValid: result.canContinue !== false,
        canContinue: result.canContinue !== false,
      },
    }));
  } catch (error) {
    setEmailValidations((prev) => ({
      ...prev,
      [fieldName]: {
        isValidating: false,
        message: 'Error al validar email',
        isValid: false,
        canContinue: false,
      },
    }));
  }
};
